import Vue from 'vue';
import {HTTP} from '@/utils/httpCommonUtil';
import VueSimpleSpinner from "vue-simple-spinner";
import {UserRoleEnum} from '@/enums/userRolesEnum';
import InvoiceEventHub from '@/components/invoice/InvoiceEventHub';
import MaskedInput from 'vue-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import {BallPulseLoader} from 'vue-loaders';
import HttpMixin from "@/mixins/HttpMixin";
import {getRestParamErrorMessage, getRestResponseErrorMessage} from "@/utils/serverErrorMessageUtil";
import TransactionType, {providerlessTransactionTypes} from "@/enums/transactionTypeEnum";

export default Vue.component('invoice-list-items', {
  name: 'InvoiceListItems',
  props: {
    invoiceId: { type: [String, Number] },
  },
  mixins: [ HttpMixin ],
  components: {
    VueSimpleSpinner,
    MaskedInput,
    BallPulseLoader
  },
  data () {
    return {
      savingNewTransaction: false,
      loadingDataCounter: 0,
      cardExpanded: false,
      transactionData: null as any,
      facilityData: [] as any[],
      originalQuantity: 0,
      quantityErrorMessage: '',
      editingAdminFee: false,
      updatingData: false,
      quantityMask: createNumberMask({
        prefix: '',
        allowDecimal: true,
        integerLimit: 6,
        decimalLimit: 2,
        thousandsSeparatorSymbol: ''
      }),
      transactionTypes: [] as any[],
      rateMask: createNumberMask({
        prefix: '',
        allowDecimal: true,
        decimalLimit: 2,
        integerLimit: 6,
        thousandsSeparatorSymbol: '',
        allowNegative: true
      }),
      specialtyRates: null as any,
      credentialedSpecialtyProviders: null as any
    };
  },
  computed: {
    isPotential (): boolean {
      return !(Number(this.invoiceId) > 0);
    },
    isFacilityUser (): boolean {
      return this.$store.getters['user/hasOnlyRole'](UserRoleEnum.FACILITY_CONTACT);
    },
    isAuthorizedToDelete() : boolean {
      return this.$store.getters['user/hasRole']([UserRoleEnum.ACCOUNTANT, UserRoleEnum.MANAGER]);
    }
  },
  async created () {
    await this.expandCard();
    if (!this.isFacilityUser) {
      this.transactionTypes = await (this as any).httpGet('/transaction-types', 'Failed to get Transaction Types') || [];
    }
    InvoiceEventHub.$on('INVOICE_CHANGE', this.dataFetchWrapper);
  },
  beforeDestroy: function () {
    InvoiceEventHub.$off('INVOICE_CHANGE', this.dataFetchWrapper);
  },
  methods: {
    // this comment copypasta is to denote that this function is to work around a leak caused by using anonymous functions in the event callback.
    // per this issue comment https://github.com/vuejs/vue-router/issues/1181#issuecomment-281084486
    // testing bears this out to be true and was causing unnecessary data fetches as well as bugs with incorrect route redirects
    dataFetchWrapper() {
      this.getSpecialTransactionInfo().then(() => {}).catch(() => '');
    },
    async expandCard (): Promise<void> {
      this.cardExpanded = !this.cardExpanded;
      if (this.cardExpanded) {
        await this.getSpecialTransactionInfo();
      }
    },
    async getSpecialTransactionInfo () {
      this.transactionData = null;
      try {
        this.loadingDataCounter++;
        let parameters;
        if (this.invoiceId) {
          parameters = `invoiceId=${this.invoiceId}`;
        }
        const response = await HTTP.get('/api/special-invoices/transactions?' + parameters) as any;
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Invoice Information.");
        }
        const responseData = response.data.data;
        responseData.transactions.forEach((specialTransaction:any) => {
          specialTransaction.providerName = '';
          specialTransaction.billRateCalculated = (+specialTransaction.billRateCalculated).toFixed(2);
          specialTransaction.payRateCalculated = (+specialTransaction.payRateCalculated).toFixed(2);
          specialTransaction.quantity = '' + (+specialTransaction.quantity).toFixed(2);
          specialTransaction.isEditing = false;
          specialTransaction.updatingData = false;
          specialTransaction.transactionTypeIdErrorMessage = '';
          specialTransaction.quantityErrorMessage = '';
          specialTransaction.providerErrorMessage = '';
          specialTransaction.billRateCalculatedErrorMessage = '';
          specialTransaction.payRateCalculatedErrorMessage = '';
          specialTransaction.isFrozen = responseData.isFrozen || specialTransaction.accountingFrozen;
        });
        this.transactionData = responseData;

      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'An error occurred retrieving Invoice Information.'
        });
      } finally {
        this.loadingDataCounter--;
      }
    },
    browseToFacility(facilityId: number) {
      window.open("/facilities/" + facilityId, '_blank');
    },
    addTransaction () {
      // Collapse any editing transactions
      // Delete any existing "new" transactions
      let indexesToDelete: any = [];
      let transactions = this.transactionData.transactions;
      transactions.forEach(function (transaction: any, index: any) {
        if (transaction.isNew) {
          indexesToDelete.push(index);
        }
        transaction.isEditing = false;
      });
      indexesToDelete.forEach(function (indx: number) {
        transactions.splice(indx,1);
      });

      let newTransaction = {
        id: 0,
        invoiceId: this.invoiceId,
        shiftTransactionId: 0,
        transactionTypeId: 0,
        transactionTypeName: '',
        transactionTypeQuantityLabel: '',
        quantity: "1",
        billRateCalculated: "0",
        payRateCalculated: "0",
        note: '',
        providerId: 0,
        transactionTypeIdErrorMessage: '',
        quantityErrorMessage: '',
        providerErrorMessage: '',
        billRateCalculatedErrorMessage: '',
        payRateCalculatedErrorMessage: '',
        isEditing: true,
        isNew: true
      };

      transactions.push(newTransaction);
    },
    toggleEditTransaction (transaction: any) {
      if (this.isFacilityUser) {
        return;
      }
      if (!transaction.isEditing) {
        this.transactionData.transactions.forEach((transaction:any) => transaction.isEditing = false);
      }
      transaction.isEditing = !transaction.isEditing;
    },
    async updateProvider(transaction: any, provider: any) {
      const newProvider = transaction.providerId !== provider.id;
      transaction.providerId = provider.id;
      transaction.providerName = provider.name;
      transaction.providerLevelId = provider.providerLevelId;
      if (newProvider && transaction.transactionTypeId) {
        await this.defaultBillAndPayRates(transaction);
      }
    },
    async cancel(transaction: any) {
      this.toggleEditTransaction(transaction);
      await this.getSpecialTransactionInfo();
    },
    async transactionTypeChange (currentTransaction: any) {
      if (providerlessTransactionTypes.some(type => type === currentTransaction.transactionTypeId)) {
        currentTransaction.providerId = null;
        currentTransaction.payRateCalculated = 0;
      }
      if (currentTransaction.providerId) {
        await this.defaultBillAndPayRates(currentTransaction);
      }
      this.transactionTypes.forEach( (transactionType: any) => {
        if (transactionType.transactionTypeId === currentTransaction.transactionTypeId) {
          currentTransaction.transactionTypeQuantityLabel = parseInt(currentTransaction.quantity, 10) === 1 ? transactionType.transactionTypeQuantityLabel : transactionType.transactionTypeQuantityPluralLabel;
        }
      });
    },
    async defaultBillAndPayRates(currentTransaction: any) {
      await this.fetchRateData();
      let defaultBillRate = null;
      let defaultPayRate = null;

      const credentialedProvider = this.credentialedSpecialtyProviders.find((credentialedProvider:any) =>
        credentialedProvider.providerId === currentTransaction.providerId
            && (credentialedProvider.transactionTypeId === currentTransaction.transactionTypeId || currentTransaction.transactionTypeId === TransactionType.HOURLY_HOLIDAY)
            && this.credentialActive(credentialedProvider)
      );
      if (credentialedProvider) {
        if (currentTransaction.transactionTypeId === TransactionType.HOURLY_HOLIDAY) {
          defaultBillRate = credentialedProvider.hourlyHolidayBillRate;
          defaultPayRate = credentialedProvider.hourlyHolidayPayRate;
        }
        defaultBillRate = defaultBillRate ? defaultBillRate : credentialedProvider.billRate;
        defaultPayRate = defaultPayRate ? defaultPayRate : credentialedProvider.payRate;
      }

      const rateData: any = this.specialtyRates.find((specialtyRate: any) =>
        specialtyRate.rateTypeId === currentTransaction.transactionTypeId && specialtyRate.providerLevelId === currentTransaction.providerLevelId);
      if (rateData) {
        defaultBillRate = defaultBillRate ? defaultBillRate : rateData.billRate;
        defaultPayRate = defaultPayRate ? defaultPayRate : rateData.payRate;
      }

      currentTransaction.billRateCalculated = (defaultBillRate ? defaultBillRate : 0) + '';
      currentTransaction.payRateCalculated = (defaultPayRate ? defaultPayRate : 0) + '';
    },
    credentialActive(credentialedProvider: any) {
      return new Date(credentialedProvider.issuedDate).getTime() <= new Date(this.transactionData.invoiceStartDate).getTime()
          && new Date(credentialedProvider.expirationDate).getTime() >= new Date(this.transactionData.invoiceStartDate).getTime()
    },
    fetchRateData() {
      const promises = [];
      if (!this.specialtyRates) {
        promises.push(this.fetchSpecialtyRateData());
      }
      if (!this.credentialedSpecialtyProviders) {
        promises.push(this.fetchCredentialedSpecialtyProviderData());
      }
      return Promise.all(promises);
    },
    async fetchCredentialedSpecialtyProviderData() {
      try {
        this.loadingDataCounter++;
        const response = await HTTP.get(`api/facilities/${this.transactionData.facilityId}/specialties/${this.transactionData.specialtyId}/credentialed-providers`);
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Specialty Credentialed Providers.");
        }
        this.credentialedSpecialtyProviders = response.data.data;
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'Error retrieving Specialty Credentialed Providers.'
        });
      } finally {
        this.loadingDataCounter--;
      }
    },
    async fetchSpecialtyRateData() {
      try {
        this.loadingDataCounter++;
        const response = await HTTP.get(`api/facilities/${this.transactionData.facilityId}/specialties/${this.transactionData.specialtyId}/rates`);
        if (!response.data || !response.data.data) {
          throw new Error("Error retrieving Specialty Rates.");
        }
        this.specialtyRates = response.data.data;
      } catch (err) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error',
          text: 'Error retrieving Specialty Rates.'
        });
      } finally {
        this.loadingDataCounter--;
      }
    },
    async createTransaction(transaction: any) {
      transaction.updatingData = true;
      try {
        await HTTP.post(`api/special-invoices/transactions`, transaction);
        this.savingNewTransaction = false;
        transaction.updatingData = false;
        InvoiceEventHub.$emit('INVOICE_CHANGE');
      } catch (err) {
        transaction.transactionTypeIdErrorMessage = getRestParamErrorMessage(err, 'transactionTypeId');
        transaction.quantityErrorMessage = getRestParamErrorMessage(err, 'quantity');
        transaction.providerErrorMessage = getRestParamErrorMessage(err, 'providerId');
        transaction.billRateCalculatedErrorMessage = getRestParamErrorMessage(err, 'billRateCalculated');
        transaction.payRateCalculatedErrorMessage = getRestParamErrorMessage(err, 'payRateCalculated');
        if (
          !transaction.transactionTypeIdErrorMessage.length &&
          !transaction.quantityErrorMessage.length &&
          !transaction.providerErrorMessage.length &&
          !transaction.billRateCalculatedErrorMessage.length &&
          !transaction.payRateCalculatedErrorMessage.length
        ) {
          let generalError = getRestResponseErrorMessage(err);
          this.$notify({
            group: 'main',
            type: 'error',
            title: 'Error Updating',
            text: generalError ? generalError : 'An error occurred updating transaction.',
            // duration: 5000
          });
        }
        transaction.updatingData = false;
      }
    },
    async updateTransaction(transaction: any) {
      transaction.updatingData = true;
      try {
        await HTTP.put(`api/special-invoices/transactions/${transaction.id}`, transaction);
        transaction.updatingData = false;
        InvoiceEventHub.$emit('INVOICE_CHANGE');
      } catch (err) {
        transaction.transactionTypeIdErrorMessage = getRestParamErrorMessage(err, 'transactionTypeId');
        transaction.quantityErrorMessage = getRestParamErrorMessage(err, 'quantity');
        transaction.providerErrorMessage = getRestParamErrorMessage(err, 'providerId');
        transaction.billRateCalculatedErrorMessage = getRestParamErrorMessage(err, 'billRateCalculated');
        transaction.payRateCalculatedErrorMessage = getRestParamErrorMessage(err, 'payRateCalculated');
        if (
          !transaction.transactionTypeIdErrorMessage.length &&
          !transaction.quantityErrorMessage.length &&
          !transaction.providerErrorMessage.length &&
          !transaction.billRateCalculatedErrorMessage.length &&
          !transaction.payRateCalculatedErrorMessage.length
        ) {
          let generalError = getRestResponseErrorMessage(err);
          this.$notify({
            group: 'main',
            type: 'error',
            title: 'Error Updating',
            text: generalError ? generalError : 'An error occurred updating transaction.',
            // duration: 5000
          });
        }
        transaction.updatingData = false;
      }
    },
    async deleteTransaction(transaction: any) {
      transaction.updatingData = true;
      await (this as any).httpDelete(`/special-invoices/transactions/${transaction.id}`, 'Failed to delete special transaction');
      InvoiceEventHub.$emit('INVOICE_CHANGE');
    },
    transactionTypeProhibitsProvider(transaction: any) {
      if (transaction) {
        return providerlessTransactionTypes.some(type => type === transaction.transactionTypeId);
      }
      return false;
    }
  }
});



// WEBPACK FOOTER //
// ./src/components/invoice/cards/lineItems/InvoiceLineItems.ts